import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Ref } from 'vue';
import { fetchCandidateFields } from '@/api/candidateFields';
import type { CandidateFieldDefinition } from '@/types/candidate';

/**
 * Caches the customer's candidate custom field definitions. This is the mechanism behind "newly
 * created fields become available via autocomplete everywhere within the customer's context" —
 * one shared list, so creating a field anywhere (the detail page or the settings page) immediately
 * shows up in every other open selector without a refetch.
 */
export const useCandidateFieldsStore = defineStore('candidateFields', () => {
  const definitions: Ref<CandidateFieldDefinition[]> = ref([]);
  const isLoading = ref(false);
  const hasLoaded = ref(false);
  let pendingFetch: Promise<void> | null = null;

  async function fetchDefinitions(force = false) {
    if (pendingFetch) return pendingFetch;
    if (hasLoaded.value && !force) return;

    isLoading.value = true;
    pendingFetch = (async () => {
      try {
        definitions.value = (await fetchCandidateFields()).sort((a, b) => a.position - b.position);
      } catch (error) {
        console.error('Error fetching candidate field definitions:', error);
      } finally {
        isLoading.value = false;
        hasLoaded.value = true;
        pendingFetch = null;
      }
    })();

    return pendingFetch;
  }

  function applyList(list: CandidateFieldDefinition[]) {
    definitions.value = [...list].sort((a, b) => a.position - b.position);
    hasLoaded.value = true;
  }

  function upsertDefinition(field: CandidateFieldDefinition) {
    const index = definitions.value.findIndex((existing) => existing.field_key === field.field_key);
    const next = [...definitions.value];
    if (index === -1) next.push(field);
    else next[index] = field;
    applyList(next);
  }

  function removeDefinition(fieldKey: string) {
    applyList(definitions.value.filter((field) => field.field_key !== fieldKey));
  }

  const sortedDefinitions = computed(() => [...definitions.value].sort((a, b) => a.position - b.position));

  return {
    definitions,
    isLoading,
    hasLoaded,
    fetchDefinitions,
    applyList,
    upsertDefinition,
    removeDefinition,
    sortedDefinitions,
  };
});
